import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManText } from '@craft-design-system/core-components-react';
import PopoverComponent from '../popover';
import SegmentedControl_totalChargingTime from './SegmentedControl_totalChargingTime';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Shimmer_graph from '../shimmer_graph';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import ifav from './ifav.png';

const Charging_duration_while_trickle_charging = ({ value }) => {
  const { vin_short, chargingDurationWhileTrickleChargingData } = value;

  const [chartType, setChartType] = useState("histogram");
  
  const [charging_duration_while_trickle_total, set_charging_duration_while_trickle_total] = useState(chargingDurationWhileTrickleChargingData?.[0] ? JSON.parse(chargingDurationWhileTrickleChargingData[0]) : { data: [], layout: {} });
  const [downloadData, setDownloadData] = useState(chargingDurationWhileTrickleChargingData?.[1] ? JSON.parse(chargingDurationWhileTrickleChargingData[1]) : []);
  const [isLoading, setIsLoading] = useState(false);
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const tooltipContent = chartType === "histogram"
    ? `
    <b>Definition</b><br/>
    
    <b>"Trickle charging"</b> usually takes place at high SoC values to ensure the target SoC at the end of the "time charger connected".

    This KPI is visualizing the <b>duration of trickle charging.</b>
    <br/>
    <b>Histogram</b><br/>

    Each bar represents the number charger connected events that fall within a certain range (bin) of trickle charging duration. 

    All duration values since start of data acquisition for the selected vehicle(s) are visualized per default.

    `
    : `Boxplot

    The boxplot shows the <b>distribution of trickle charging duration</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the combined distribution of all their values.
    <ul>
    <li>The box represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<li>
    <li>The line inside the box shows the median (50th percentile).</li>
    <li>The whiskers extend to show the range of typical values ("min" and "max") and visualize the spread of the data beyond the central box.</li>
    </ul>`;

    const popover = (
      <Popover id="popover-basic">
        <Popover.Body>
          <strong>Definition:</strong><br />
          "Trickle charging" usually takes place at high SoC values to ensure the target SoC at the end of the "time charger connected".<br />
          This KPI is visualizing the duration of trickle charging.<br /><br />
    
          <strong>Telematic raw data for this KPI:</strong><br />
          TBM3:<br />
          • signal_timestamp<br />
          CM4:<br />
          • signal_timestamp<br /><br />
    
          <strong>Histogram:</strong><br />
          Each bar represents the number of charger connected events that fall within a certain range (bin) of trickle charging duration.<br />
          All duration values since start of data acquisition for the selected vehicle(s) are visualized per default.<br /><br />
    
          <strong>Boxplot:</strong><br />
          The boxplot shows the distribution of trickle charging duration for the selected vehicle(s) since start of data acquisition.<br />
          If multiple vehicles are selected, the boxplot reflects the combined distribution of all their values.<br />
          • The <strong>box</strong> represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<br />
          • The line <strong>inside the box</strong> shows the median (50th percentile).<br />
          • The whiskers extend to show the range of typical values ("min" and "max") and visualize <strong>the spread of the data</strong> beyond the central box.
        </Popover.Body>
      </Popover>
    );

  // Load fallback data only once
  useEffect(() => {
    if (!charging_duration_while_trickle_total && chargingDurationWhileTrickleChargingData) {
      try {
        const parsed = JSON.parse(chargingDurationWhileTrickleChargingData);
        set_charging_duration_while_trickle_total(parsed);
      } catch (error) {
        console.error('Error parsing fallback data JSON', error);
      }
    }
  }, [chargingDurationWhileTrickleChargingData, charging_duration_while_trickle_total]);

  // Fetch chart and download data
  useEffect(() => {
    const fetchData = async () => {
      if (!vin_short?.length) return;

      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: 'Charging Duration While Trickle Charging',
        });

        set_charging_duration_while_trickle_total(JSON.parse(data[0]));   // chart
        setDownloadData(JSON.parse(data[1]));        // download
      } catch (error) {
        console.error('Error fetching chart data:', error);
        set_charging_duration_while_trickle_total({ data: [], layout: {} });
        setDownloadData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, JSON.stringify(vin_short)]);

  return (
    <div style={{ padding: '16px' }}>
      {/* Title and Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            Charging duration while trickle charging
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse",
          gap: "8px"
        }}>
          {/* <PopoverComponent
            id="charging_time"
            content={tooltipContent}
          /> */}

          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img className="man-logovv1" src={ifav} alt="info" style={{ width: '20px', height: '20px', margin: 0 }} />
          </OverlayTrigger>
          <Zoom
            chartData={charging_duration_while_trickle_total?.data}
            chartLayout={charging_duration_while_trickle_total?.layout}
            chartTitle="Charging duration while trickle charging"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`Charging_Duration_While_Trickle_Charging_${chartType.toLowerCase()}.xlsx`}
            disabled={!downloadData || downloadData.length === 0}
          />
        </div>
      </div>

      {/* Segmented Control */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SegmentedControl_totalChargingTime
          selected={chartType}
          onChange={setChartType}
          vinShort={Array.isArray(vin_short) ? vin_short : [vin_short]}
          allowedTypes={["histogram", "boxplot"]}
        />
      </div>

      {/* Chart */}
      {isLoading ? (
        <Shimmer_graph />
      ) : charging_duration_while_trickle_total?.data && charging_duration_while_trickle_total?.layout ? (
        <div style={{ width: '100%', height: '420px', overflow: 'hidden' }}>
          <div style={{ width: '100%', height: '100%' }}>
            <Plot
              data={charging_duration_while_trickle_total.data}
              layout={{
                ...charging_duration_while_trickle_total.layout,
                autosize: true,
                responsive: true,
                margin: { t: 10, r: 10, l: 10, b: 160 },
              }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
            />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <ManText className='card-title-text' variant='component-l' as='span' size='l'>
            {charging_duration_while_trickle_total ? `${charging_duration_while_trickle_total} km` : 'No Data Available'}
          </ManText>
        </div>
      )}
    </div>
  );
};

Charging_duration_while_trickle_charging.propTypes = {
  value: PropTypes.shape({
    chargingDurationWhileTrickleChargingData: PropTypes.string,
    vin_short: PropTypes.array,
  }).isRequired,
};

export default Charging_duration_while_trickle_charging;
